import Paper from "@mui/material/Paper";
import ContentBody from "../common/content-body/ContentBody";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { useForm } from "react-hook-form";
import { IExpiredActionExecuteParams, IExpiredWarningLetterForm } from "../models/models";
import { FormTextField } from "../common/form-fields-wrappers";
import { EXPIRED_PRACTICES_QUERY_KEY, EXPIRED_WARNING_LETTER_FORM_INITIAL_VALUE } from "../config";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { expiredActionPropsState, practiceDetail } from "../state";
import { useCallback, useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { useMutation } from "@tanstack/react-query";
import { executeExpiredAction, generateWarningLetter} from "../services/http.services";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { setSnackbar } from "../../../util/state/globalAction";
import CustomLoader from "../../../util/component/CustomLoader";
import { blobToBase64String } from "blob-util";

interface WarningLetterProps {
    onConfirm: () => void,
    onClose: () => void
}
const WarningLetter = ({...props}: WarningLetterProps) => {

    const {t, i18n} = useTranslation(['expiredManagement']);
    const theme = useTheme();
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);
    
    const warningLetterForm = useForm<IExpiredWarningLetterForm>({
        defaultValues: EXPIRED_WARNING_LETTER_FORM_INITIAL_VALUE
    });

    const [warningLetterDetail, setWarningLetterDetail] = useState<any>();

    const expiredActionProps = useRecoilValue(expiredActionPropsState);
    const setPracticeDetail = useSetRecoilState(practiceDetail);

    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message, t('common.message.error.tryLater')),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const onConfirm = useCallback(() => {

        const req: IExpiredActionExecuteParams = {
            permissionData: {
                mobilityStores: globalData?.user?.mobilityStores?.map(((elem:any) => elem.sapId)),
                authorities: globalData?.user?.scopes
            },
            action: expiredActionProps?.actionType || '',
            expiredCodes: expiredActionProps?.expiredCodes || [],
            language: i18n.languages?.at(0) || '',
        }

        executeMutation.mutate(req);

    }, [globalData, expiredActionProps, i18n]);

    const generateWarningLetterMutation = useMutation(
        {
            mutationFn: (params: string) => {
                
                return generateWarningLetter(params);
            },
            onSuccess: async (response) => {
                
                var blob = null;
                if(!!response)
                    blob = new Blob([response]);

                var base64 = '';
                if(!!blob){
                    await blobToBase64String(blob).then(converted => {
                        base64 = converted;
                    }).catch((err) => console.log("ERR: ", err));
                };
                
                setWarningLetterDetail({
                    companyName: expiredActionProps?.raDetails?.companyName || '',
                    customerNameSurname: expiredActionProps?.raDetails?.customerNameSurname || '',
                    customerEmail: expiredActionProps?.raDetails?.customerEmail || '',
                    customerSapId: expiredActionProps?.raDetails?.clientId || '',
                    file: base64 || ''
                });

            },
            onError: (error: any) => {
                viewSnackbarMessage(`common.message.error.lawsuitpdf.${error?.response?.status}`, snackbarTypes.error);
                props.onClose();
                
            }
        }
    );

    const executeMutation = useMutation(
        {
            mutationKey: [EXPIRED_PRACTICES_QUERY_KEY.EXECUTE_ACTION],
            mutationFn: (params: IExpiredActionExecuteParams) => {
                return executeExpiredAction(params);
            },
            onSuccess: (response) => {
                viewSnackbarMessage('common.message.success', snackbarTypes.success);
                
                if(!!response?.data)
                    setPracticeDetail(response.data);

                props.onConfirm();
            },
            onError: (error: any) => {
                viewSnackbarMessage(`common.message.error.execute.${error?.response?.status}`, snackbarTypes.error);
            }
        }
    );

    const setFormData = () => {
        warningLetterForm.setValue("companyName", warningLetterDetail?.companyName || '');
        warningLetterForm.setValue("customerNameSurname", warningLetterDetail?.customerNameSurname || '');
        warningLetterForm.setValue("customerEmail", warningLetterDetail?.customerEmail || '');
        warningLetterForm.setValue("customerSapId", warningLetterDetail?.customerSapId || '');
        warningLetterForm.setValue("file", warningLetterDetail?.file || '');
    }

    useEffect(() => {
        if(!!warningLetterDetail) {
            setFormData();
        }
    }, [warningLetterDetail]);

    useEffect(() => {
        if(!!expiredActionProps && expiredActionProps?.expiredCodes?.length > 0)
            generateWarningLetterMutation.mutate(expiredActionProps.expiredCodes[0]);

    },[]);

    return (
        <>
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2)}}>
                    <Grid container spacing={theme.spacing(2)}>
                        <Grid item xs={12} md={6}>
                            <FormTextField<IExpiredWarningLetterForm>
                                name='customerSapId'
                                control={warningLetterForm.control}
                                label={t('expiredDetail.form.customerSapId')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {!!warningLetterDetail?.companyName ?
                                <FormTextField<IExpiredWarningLetterForm>
                                    name='companyName'
                                    control={warningLetterForm.control}
                                    label={t('expiredDetail.form.companyName')}
                                    fullWidth
                                    disabled
                                />
                                :
                                <FormTextField<IExpiredWarningLetterForm>
                                    name='customerNameSurname'
                                    control={warningLetterForm.control}
                                    label={t('expiredDetail.form.customerNameSurname')}
                                    fullWidth
                                    disabled
                                />
                            }
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormTextField<IExpiredWarningLetterForm>
                                name='customerEmail'
                                control={warningLetterForm.control}
                                label={t('expiredDetail.form.customerEmail')}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} pt={theme.spacing(1)} pb={theme.spacing(1)}>      
                            <div style={{position: 'relative', paddingBottom: '100%'}}>
                                <iframe
                                    loading={"lazy"}
                                    style={{position: "absolute", left: 0, top:0, height: '100%', width:'100%', backgroundColor: '#525659'}} 
                                    src={!!warningLetterDetail?.file ? `data:application/pdf;base64,${warningLetterDetail.file}` : undefined}
                                />
                            </div>      
                        </Grid>

                    </Grid>

                    <Stack 
                        direction={{sm: 'row'}} 
                        gap={1}      
                        sx={{display: "flex", justifyContent: "flex-end", mt: theme.spacing(2), mb: theme.spacing(1)}}
                    >
                        <Button 
                            onClick={warningLetterForm.handleSubmit(onConfirm)}>
                            {t('common.button.send')}
                        </Button>
                    </Stack>
                </Paper>
                <CustomLoader loading={generateWarningLetterMutation.isLoading || executeMutation.isLoading} />
            </ContentBody>
        </>
    )
}

export default WarningLetter;